import { useState } from 'react';
import { api } from '../../lib/api';
import { T, useGym } from '../../lib/gym';
import { useI18n } from '../../lib/i18n';
import { AsyncButton, Button, Card, Field, Input, Select, Textarea, useToast } from '../../ui/components';
import { useOptions } from '../../ui/schema';

interface Segment { stage?: string; membershipStatus?: string; locationId?: string; inactiveDays?: number | null; joinedWithinDays?: number | null; lowTokens?: boolean }
const PRESETS: { key: string; segment: Segment }[] = [
  { key: 'inactive_members', segment: { stage: 'member', inactiveDays: 30 } },
  { key: 'trial_not_converted', segment: { stage: 'trial', joinedWithinDays: 60 } },
  { key: 'out_of_tokens', segment: { stage: 'member', lowTokens: true } },
  { key: 'leads_no_trial', segment: { stage: 'lead' } },
  { key: 'former_members', segment: { stage: 'former_member' } },
  { key: 'all_members', segment: { stage: 'member' } },
];

/** Writes one message to a selected group of clients (marketing, so consent is respected). */
export function CampaignComposer({ onSent }: { onSent(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const plans = useOptions(gym.slug, 'membership_plans');
  const [channel, setChannel] = useState('email');
  const [segment, setSegment] = useState<Segment>({ stage: 'member', inactiveDays: 30 });
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [preview, setPreview] = useState<{ count: number; sample: { name: string; email: string }[] } | null>(null);

  const check = async (next: Segment = segment, ch = channel): Promise<void> => {
    setPreview(await api(`${T(gym.slug)}/admin/campaigns/preview`, { body: { segment: next, channel: ch } }));
  };
  const set = (patch: Segment): void => { const next = { ...segment, ...patch }; setSegment(next); void check(next); };

  return (
    <div className="stack">
      <Card title={i18n.t('messages.who')}>
        <div className="row gap-sm wrap">
          {PRESETS.map((p) => (
            <button type="button" key={p.key} className="chip" onClick={() => { setSegment(p.segment); void check(p.segment); }}>{i18n.t(`messages.preset_${p.key}`)}</button>
          ))}
        </div>
        <div className="grid-3">
          <Field label={i18n.field('stage')}>
            <Select value={segment.stage ?? ''} placeholder={i18n.t('common.all')} onChange={(e) => set({ stage: e.target.value || undefined })}
              options={['lead', 'trial', 'member', 'former_member'].map((s) => ({ value: s, label: i18n.option(s) }))} />
          </Field>
          <Field label={i18n.t('messages.inactive_days')}>
            <Input type="number" min={0} value={segment.inactiveDays ?? ''} onChange={(e) => set({ inactiveDays: e.target.value ? Number(e.target.value) : null })} />
          </Field>
          <Field label={i18n.t('messages.joined_days')}>
            <Input type="number" min={0} value={segment.joinedWithinDays ?? ''} onChange={(e) => set({ joinedWithinDays: e.target.value ? Number(e.target.value) : null })} />
          </Field>
          {gym.locations.length > 1 && (
            <Field label={i18n.field('location_id')}>
              <Select value={segment.locationId ?? ''} placeholder={i18n.t('planner.all_locations')} onChange={(e) => set({ locationId: e.target.value || undefined })}
                options={gym.locations.map((l) => ({ value: l.id, label: l.name }))} />
            </Field>
          )}
          <Field label={i18n.field('status')} hint={i18n.t('messages.membership_hint')}>
            <Select value={segment.membershipStatus ?? ''} placeholder={i18n.t('common.all')} onChange={(e) => set({ membershipStatus: e.target.value || undefined })}
              options={['active', 'paused', 'suspended', 'cancelled'].map((s) => ({ value: s, label: i18n.option(s) }))} />
          </Field>
          <Field label={i18n.t('messages.low_tokens')}>
            <Select value={segment.lowTokens ? 'yes' : ''} placeholder={i18n.t('common.all')} onChange={(e) => set({ lowTokens: e.target.value === 'yes' })}
              options={[{ value: 'yes', label: i18n.t('messages.low_tokens_yes') }]} />
          </Field>
        </div>
        <div className="row gap-sm">
          <Button variant="secondary" onClick={() => check()}>{i18n.t('messages.count')}</Button>
          {preview && <span>{i18n.t('messages.recipients', { count: preview.count })}{preview.sample.length ? ` — ${preview.sample.map((s) => s.name).join(', ')}…` : ''}</span>}
        </div>
        {plans.length === 0 ? null : <p className="small muted">{i18n.t('messages.consent_note')}</p>}
      </Card>

      <Card title={i18n.t('messages.message')}>
        <div className="grid-2">
          <Field label={i18n.field('channel')}>
            <Select value={channel} onChange={(e) => { setChannel(e.target.value); void check(segment, e.target.value); }}
              options={['email', 'push', 'sms'].map((c) => ({ value: c, label: i18n.option(c) }))} />
          </Field>
          <Field label={i18n.field('name')} hint={i18n.t('messages.name_hint')}><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        </div>
        {channel === 'email' && <Field label={i18n.field('subject')}><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>}
        <Field label={i18n.field('body')} hint={i18n.t('messages.placeholders')}>
          <Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <AsyncButton disabled={!body.trim() || (channel === 'email' && !subject.trim())}
          confirm={i18n.t('messages.send_confirm', { count: preview?.count ?? 0 })}
          onClick={async () => {
            const r = await api(`${T(gym.slug)}/admin/campaigns/send`, { body: { name, channel, segment, subject, body } });
            toast.ok(i18n.t('messages.sent', { count: r.recipients }));
            setBody('');
            setSubject('');
            onSent();
          }}>{i18n.t('messages.send')}</AsyncButton>
      </Card>
    </div>
  );
}
